/**
 * Copyright Highway9 Networks Inc.
 */
import React, { useEffect, type JSX } from 'react';
import { Route, Redirect, } from 'react-router-dom';
import { authenticationService } from '../services';
import { routes } from '~/constants/routes';
import { useAppDispatch, useAppSelector } from '~/store/hooks';
import { Backdrop, CircularProgress } from '@h9/ui-lib';
import { fetchTenants } from '~/store/context/tenantSlice';
import { fetchPartners } from '~/store/context/partnerSlice';

// ComponentType so lazy components pass directly - an inline arrow wrapper is a
// new component type per render and remounts the whole authenticated tree
type Props =
    {
        component: React.ComponentType<any>,
        path: string,
    }

export const AuthRouter = ({ component: Component, path }: Props) => {
    const url = window.location.href;
    if (!url.includes("/login")) {
        sessionStorage.setItem("url", url);
    }
    const currentUser = authenticationService.currentUserValue;
    const routesArray = routes()

    // Check if currentUser is undefined or is an empty object ({}, which has no keys)
    const isValidCurrentUser = currentUser && Object.keys(currentUser).length > 0;

    const isTenantLoading = useAppSelector((state) => state.tenant.loading);
    const isPartnerLoading = useAppSelector((state) => state.partner.loading);
    const dispatch = useAppDispatch()

    useEffect(()=> {
        if(isValidCurrentUser && authenticationService.isTenant()){
            dispatch(fetchTenants())
        }
        if(isValidCurrentUser && authenticationService.isPartner()){
            dispatch(fetchPartners())
        }
    }, [dispatch, isValidCurrentUser])
    // const currentUser = JSON.parse(localStorage.getItem("currentUser") || 'null')
    return (
        <Route
            path={path}
            render={(props) => {

                if (!isValidCurrentUser) {
                    // not logged in so redirect to login page with the return url
                    return <Redirect to={{ pathname: '/login', state: { from: props.location } }} />
                } else {
                    // check if the user is tenant or not and redirect to the appropriate page
                    if (authenticationService.isTenant()) {
                        if (isTenantLoading) {
                            return <Backdrop sx={{ color: "#fff", zIndex: "9999" }} open={true}>
                                <CircularProgress />
                            </Backdrop>
                        } else {
                            if (!currentUser.isEULAAccepted) return <Redirect to={{ pathname: "/eula" }} />;
                        }
                    }

                    if (authenticationService.isPartner()) {
                        if (isPartnerLoading) {
                            return <Backdrop sx={{ color: "#fff", zIndex: "9999" }} open={true}>
                                <CircularProgress />
                            </Backdrop>
                        } else {
                            if (!currentUser.isEULAAccepted) return <Redirect to={{ pathname: "/eula" }} />;
                        }
                    }
                    // check if the user is operator or not and redirect to the appropriate page
                    if (!authenticationService.isOperator()) {
                        const path = window.location.pathname;
                        const currentRoute = routesArray.find((route) => route.path === path);
                        if (currentRoute?.hidden) {
                            window.location.replace("/");
                        }
                    }
                }
                // authorised so return component
                return <Component {...props} />
            }} />)
}
